<script setup lang="ts" generic="T extends string | number">
import { computed } from 'vue';

// 选项项接口定义
export interface OptionItem<K> {
  key: K;
  label: string;
}

// 组件属性
const props = withDefaults(
  defineProps<{
    options: OptionItem<T>[]; // 选项列表
    modelValue: T; // 当前选中的值
    size?: 'default' | 'large'; // 尺寸大小：default(14px) 或 large(16px)
  }>(),
  {
    size: 'default',
  },
);

// 事件定义
const emit = defineEmits<{
  'update:modelValue': [value: T];
}>();

// 当前选中值的计算属性
const currentValue = computed({
  get: () => props.modelValue,
  set: (value: T) => {
    emit('update:modelValue', value);
  },
});

// 处理选项点击事件
const handleItemClick = (option: OptionItem<T>) => {
  currentValue.value = option.key;
};
</script>

<template>
  <div class="option-switcher" :class="`option-switcher--${size}`">
    <div
      v-for="option in options"
      :key="option.key"
      class="option-switcher__item"
      :class="{ 'option-switcher__item--active': currentValue === option.key }"
      @click="handleItemClick(option)"
    >
      <div v-if="$slots.icon" class="option-switcher__icon">
        <slot name="icon" :option="option" :active="currentValue === option.key" />
      </div>
      <div class="option-switcher__text">{{ option.label }}</div>
    </div>
  </div>
</template>

<style scoped lang="scss">
// 选项切换器容器
.option-switcher {
  display: flex;
}

// 选项项
.option-switcher__item {
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;
  color: var(--x-text-5);
  transition: color 0.2s ease;
}

// 选项项悬浮和激活状态
.option-switcher__item:hover,
.option-switcher__item--active {
  color: var(--x-text-1);
  font-weight: var(--x-weight-medium);
}

// 选项图标
.option-switcher__icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

// 选项文本
.option-switcher__text {
  white-space: nowrap;
}

// 默认尺寸
.option-switcher--default {
  font-size: 14px;
  gap: 12px;
}

// 大尺寸
.option-switcher--large {
  font-size: 16px;
  gap: 20px;
}
</style>
